import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Calendar,
  CheckCircle,
  Upload,
  FileText,
  ArrowLeft,
  CheckCircle2,
  Send,
  Paperclip,
  Maximize,
  X,
  Clock,
  AlertTriangle,
  RotateCcw,
  Menu,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCourse } from "../../../context/CourseContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  getAllCourseAssignments,
  submitAssignment,
} from "../../../services/assignment.service";
import toast from "react-hot-toast";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  statNumber,
  dividerInner,
  iconSize,
  emptyState,
  badgeBase,
  statusStyles,
  accentBarSection,
  motionStaggerChild,
  pageBg,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";
import { getCardColor } from "../../../utils/courseHelpers";

const StudentAssignmentSection = ({ courseID, selectedID }) => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [subjectiveAnswers, setSubjectiveAnswers] = useState({});
  const [objectiveAnswers, setObjectiveAnswers] = useState({});
  const [activeTab, setActiveTab] = useState('questions'); // 'questions' or 'attachments'
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { courseData } = useCourse();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [assignmentStarted, setAssignmentStarted] = useState(false);
  const [warned5min, setWarned5min] = useState(false);
  const [warned1min, setWarned1min] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const timerIntervalRef = useRef(null);

  // Fetch assignments from API
  const fetchAssignments = async () => {
    if (!courseID) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await getAllCourseAssignments({ courseID });
      const courseAssignments = response.assignments || [];
      setAssignments(courseAssignments);

      // Find selected assignment
      if (courseAssignments.length > 0) {
        let found = null;
        if (selectedID) {
          // Try to find by ID first
          found = courseAssignments.find(a => a._id === selectedID);
        }
        // If not found by ID, use first assignment
        if (!found) {
          found = courseAssignments[0];
        }

        if (found) {
          setSelectedAssignment(found);
          // Load submission from assignment object
          loadSubmissionFromAssignment(found);
        }
      }
    } catch (err) {
      console.error("Error fetching assignments:", err);
      if (err.response?.status === 403) {
        toast.error("This assignment is not available for your branch");
        setError("This assignment is not available for your branch");
      } else {
        const errorMessage = err.response?.data?.message || "Failed to load assignments.";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load submission from assignment object (studentSubmission field from API)
  const loadSubmissionFromAssignment = (assignment) => {
    if (!assignment) return;

    // The API returns studentSubmission field for students
    const submissionData = assignment.studentSubmission || null;
    setSubmission(submissionData);

    // Load answers from submission if it exists
    if (submissionData?.answers) {
      setSubjectiveAnswers(submissionData.answers.subjective || {});
      setObjectiveAnswers(submissionData.answers.objective || {});
    } else {
      // Clear answers if no submission
      setSubjectiveAnswers({});
      setObjectiveAnswers({});
    }
  };

  // Check if assignment has been started
  const checkAssignmentStarted = (assignment) => {
    if (!assignment?.completeIn || submission) return false;

    const startedKey = `assignment_${assignment._id}_started`;
    const started = localStorage.getItem(startedKey) === 'true';

    if (started) {
      setAssignmentStarted(true);
      return true;
    }

    return false;
  };

  // Handle starting the assignment
  const handleStartAssignment = (assignment) => {
    if (!assignment?.completeIn || submission) return;

    const startedKey = `assignment_${assignment._id}_started`;
    localStorage.setItem(startedKey, 'true');
    setAssignmentStarted(true);

    // Now start the timer
    startTimer(assignment);
  };

  // Timer functions
  const startTimer = (assignment) => {
    if (!assignment?.completeIn || submission) return; // Don't start if already submitted

    const storageKey = `assignment_${assignment._id}_deadline`;
    const existingDeadline = localStorage.getItem(storageKey);

    if (existingDeadline) {
      // Timer already started - resume countdown
      const deadline = parseInt(existingDeadline);
      const now = Date.now();

      if (now >= deadline) {
        // Timer expired - auto submit
        handleAutoSubmit();
        return;
      }

      setTimerStarted(true);
      updateTimer(deadline);
    } else {
      // First time starting - start the timer
      const startTime = Date.now();
      const durationMs = assignment.completeIn * 60 * 1000; // Convert minutes to milliseconds
      const deadline = startTime + durationMs;

      localStorage.setItem(storageKey, deadline.toString());
      setTimerStarted(true);
      updateTimer(deadline);
    }
  };

  const updateTimer = (deadline) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const remaining = deadline - now;

      if (remaining <= 0) {
        // Time's up!
        clearInterval(timerIntervalRef.current);
        setIsTimeUp(true);
        handleAutoSubmit();
        return;
      }

      setTimeRemaining(remaining);

      // Check for warnings
      const minutesLeft = Math.floor(remaining / (1000 * 60));
      const secondsLeft = Math.floor((remaining % (1000 * 60)) / 1000);

      if (minutesLeft === 5 && secondsLeft === 0 && !warned5min) {
        toast.error('⚠️ 5 minutes remaining! Please save your work.');
        setWarned5min(true);
      }

      if (minutesLeft === 1 && secondsLeft === 0 && !warned1min) {
        toast.error('⚠️ 1 minute remaining! Your assignment will be submitted automatically.');
        setWarned1min(true);
      }
    }, 1000);

    // Initial update
    const now = Date.now();
    const remaining = deadline - now;
    setTimeRemaining(remaining);
  };

  const handleAutoSubmit = async () => {
    if (submission || isTimeUp) return; // Don't submit if already submitted or already processing

    setIsTimeUp(true);
    toast.error('Time is up! Your assignment will be submitted automatically.');

    // Clear timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    if (selectedAssignment) {
      const storageKey = `assignment_${selectedAssignment._id}_deadline`;
      localStorage.removeItem(storageKey);
    }

    // Auto-submit with current answers
    try {
      setIsLoading(true);

      // Create FormData for submission
      const formData = new FormData();

      // Check if there are any answers to send
      const hasSubjectiveAnswers = Object.keys(subjectiveAnswers).length > 0 &&
        Object.values(subjectiveAnswers).some(answer => answer?.trim());
      const hasObjectiveAnswers = Object.keys(objectiveAnswers).length > 0;

      // Only add answers if there are actual answers (not empty objects)
      if (hasSubjectiveAnswers || hasObjectiveAnswers) {
        formData.append('answers', JSON.stringify({
          subjective: subjectiveAnswers,
          objective: objectiveAnswers
        }));
      }

      // Add file if selected
      if (selectedFile && selectedFile instanceof File) {
        formData.append('submissionFile', selectedFile);
      }

      // Submit to API
      await submitAssignment(selectedAssignment._id, formData);

      // Refresh assignments to get updated submission data
      await fetchAssignments();

      // Clear file selection
      setSelectedFile(null);

      toast.success('Assignment submitted automatically due to time limit.');
    } catch (error) {
      console.error('Error auto-submitting:', error);
      toast.error('Failed to auto-submit. Please submit manually.');
      setIsTimeUp(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (ms) => {
    if (!ms || ms <= 0) return '00:00:00';

    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Initialize assignment state when assignment is selected
  useEffect(() => {
    if (selectedAssignment && !submission) {
      // Reset warning flags when assignment changes
      setWarned5min(false);
      setWarned1min(false);
      setIsTimeUp(false);

      // Check if assignment has been started
      if (selectedAssignment.completeIn) {
        const hasStarted = checkAssignmentStarted(selectedAssignment);
        if (hasStarted) {
          // Assignment already started - resume timer
          startTimer(selectedAssignment);
        } else {
          // Assignment not started yet - wait for user to click "Start Assignment"
          setAssignmentStarted(false);
        }
      } else {
        // No timer - assignment is always "started"
        setAssignmentStarted(true);
      }
    } else {
      setAssignmentStarted(true); // If submitted or no assignment, allow viewing
    }

    // Cleanup on unmount or assignment change
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [selectedAssignment?._id, submission]);

  useEffect(() => {
    if (courseID) {
      fetchAssignments();
    }
  }, [courseID, selectedID]);

  // Reset selected attachment index and set default tab when assignment changes
  useEffect(() => {
    if (selectedAssignment) {
      setSelectedAttachmentIndex(0);
      const assignmentQuestions = selectedAssignment.questions || [];
      const assignmentAttachments = selectedAssignment.attachments || [];
      const hasQuestions = assignmentQuestions.length > 0;
      const hasAttachments = assignmentAttachments.length > 0;

      // Set default tab: questions if available, otherwise attachments if available
      if (hasQuestions) {
        setActiveTab('questions');
      } else if (hasAttachments) {
        setActiveTab('attachments');
      } else {
        setActiveTab('questions');
      }
    }
  }, [selectedAssignment?._id]);

  // Handle file selection
  const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
  const MAX_FILE_SIZE_MB = 10;

  const validateFile = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return "Invalid file type. Please upload PDF, DOC, DOCX, JPEG, or PNG.";
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB`;
    }
    return null;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  // Handle subjective answer change
  const handleSubjectiveAnswerChange = (questionId, answer) => {
    setSubjectiveAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Handle objective answer change
  const handleObjectiveAnswerChange = (questionId, answer) => {
    setObjectiveAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Handle assignment selection
  const handleAssignmentSelect = (assignment, index) => {
    setSelectedAssignment(assignment);
    setSubjectiveAnswers({});
    setObjectiveAnswers({});
    setSelectedFile(null);
    setMobileSidebarOpen(false);
    // Load submission from assignment object
    loadSubmissionFromAssignment(assignment);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedAssignment) {
      toast.error("Please select an assignment");
      return;
    }

    const questions = selectedAssignment.questions || [];
    const subjectiveQuestions = questions.filter(q => q.type === 'subjective');
    const objectiveQuestions = questions.filter(q => q.type === 'objective');

    // Validate subjective questions
    if (subjectiveQuestions.length > 0) {
      const hasTextAnswers = Object.keys(subjectiveAnswers).some(
        key => subjectiveAnswers[key]?.trim()
      );
      if (!hasTextAnswers && !selectedFile) {
        toast.error("Please provide answers for subjective questions (text or PDF)");
        return;
      }
    }

    // Validate objective questions
    if (objectiveQuestions.length > 0) {
      const allAnswered = objectiveQuestions.every(q => {
        const qId = q._id || q.id;
        return objectiveAnswers[qId];
      });
      if (!allAnswered) {
        toast.error("Please answer all objective questions");
        return;
      }
    }

    try {
      setIsLoading(true);

      // Create FormData for submission
      const formData = new FormData();

      // Check if there are any answers to send
      const hasSubjectiveAnswers = Object.keys(subjectiveAnswers).length > 0 &&
        Object.values(subjectiveAnswers).some(answer => answer?.trim());
      const hasObjectiveAnswers = Object.keys(objectiveAnswers).length > 0;

      // Only add answers if there are actual answers (not empty objects)
      if (hasSubjectiveAnswers || hasObjectiveAnswers) {
        formData.append('answers', JSON.stringify({
          subjective: subjectiveAnswers,
          objective: objectiveAnswers
        }));
      }

      // Add file if selected - ensure it's a valid File object
      if (selectedFile && selectedFile instanceof File) {
        formData.append('submissionFile', selectedFile);
        console.log('Adding file to FormData:', {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        });
      } else if (selectedFile) {
        console.warn('Selected file is not a valid File object:', selectedFile);
        toast.error('Invalid file selected. Please choose a file again.');
        setIsLoading(false);
        return;
      }

      // Validate that we have either answers or a file
      if (!hasSubjectiveAnswers && !hasObjectiveAnswers && !selectedFile) {
        toast.error('Please provide answers or upload a file');
        setIsLoading(false);
        return;
      }

      // Log FormData contents for debugging
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        if (pair[1] instanceof File) {
          console.log(`${pair[0]}: File - ${pair[1].name} (${pair[1].size} bytes, type: ${pair[1].type})`);
        } else {
          console.log(`${pair[0]}: ${pair[1]}`);
        }
      }

      // Submit to API
      const response = await submitAssignment(selectedAssignment._id, formData);

      // Clear timer if assignment had a time limit
      if (selectedAssignment.completeIn) {
        const storageKey = `assignment_${selectedAssignment._id}_deadline`;
        localStorage.removeItem(storageKey);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
        setTimerStarted(false);
        setTimeRemaining(null);
      }

      // Refresh assignments to get updated submission data
      await fetchAssignments();

      // Clear file selection
      setSelectedFile(null);

      // Success message is handled in the service
    } catch (error) {
      // Error is already handled in the service with toast
      console.error("Error submitting assignment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "No due date";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  // Short date for sidebar metadata line
  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch (e) {
      return "";
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh]">
        {/* Sidebar skeleton */}
        <div className="hidden md:block w-60 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 pr-4">
          <div className="pt-2">
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse mb-1.5" />
            <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse mb-4" />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="py-3 px-3 animate-pulse">
              <div className="h-3.5 w-4/5 bg-gray-200 dark:bg-gray-700 rounded-sm mb-2" />
              <div className="flex gap-2">
                <div className="h-4 w-14 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                <div className="h-3 w-10 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
              </div>
            </div>
          ))}
        </div>
        {/* Main content skeleton */}
        <div className="flex-1 min-w-0 md:pl-5 space-y-4">
          <div className={cn(card, "overflow-hidden")}>
            <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
            <div className="p-5 space-y-3">
              <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
              <div className="h-7 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
              <div className="flex gap-3">
                <div className="h-3 w-24 bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                <div className="h-3 w-16 bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
              </div>
            </div>
          </div>
          {[1, 2].map((i) => (
            <div key={i} className={cn(card, "p-5 space-y-3")}>
              <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
              <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-sm p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                  <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                </div>
                <div className="h-16 w-full bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn(emptyState, "m-6")}>
        <AlertTriangle className={cn(iconSize.empty, "mx-auto mb-3 text-red-400")} />
        <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  // No assignments
  if (!assignments || assignments.length === 0) {
    return (
      <div className={cn(emptyState, "m-6")}>
        <FileText className={cn(iconSize.empty, "mx-auto mb-3 text-gray-400 dark:text-gray-500")} />
        <p className={cn(cardTitle, "mb-1")}>No assignments found</p>
        <p className={bodySecondary}>Assignments will appear here when your teacher creates them.</p>
      </div>
    );
  }

  // No selected assignment
  if (!selectedAssignment) {
    return (
      <div className={cn(emptyState, "m-6")}>
        <FileText className={cn(iconSize.empty, "mx-auto mb-3 text-gray-400 dark:text-gray-500")} />
        <p className={cn(cardTitle, "mb-1")}>Select an assignment</p>
        <p className={bodySecondary}>Choose an assignment from the sidebar to get started.</p>
      </div>
    );
  }

  // Separate questions by type
  const questions = selectedAssignment.questions || [];
  const subjectiveQuestions = questions.filter(q => q.type === 'subjective');
  const objectiveQuestions = questions.filter(q => q.type === 'objective');

  // Get attachments from assignment
  const attachments = selectedAssignment.attachments || [];
  const hasAttachments = attachments.length > 0;

  // Helper function to get file type from attachment
  const getFileType = (attachment) => {
    if (!attachment.type && !attachment.name) return 'unknown';
    const type = attachment.type?.toLowerCase() || '';
    const name = attachment.name?.toLowerCase() || '';

    if (type.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
    if (type.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(name)) return 'image';
    if (type.includes('powerpoint') || type.includes('presentation') || name.endsWith('.ppt') || name.endsWith('.pptx')) return 'ppt';
    if (type.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return 'doc';
    return 'other';
  };

  // Helper function to get file URL
  const getFileUrl = (attachment) => {
    return attachment.url || attachment.fileUrl || '';
  };

  // Render attachment viewer content
  const renderAttachmentViewer = (attachment, showFullscreenButton = false) => {
    if (!attachment) return null;

    const fileType = getFileType(attachment);
    const fileUrl = getFileUrl(attachment);
    const fileName = attachment.name || `Attachment ${selectedAttachmentIndex + 1}`;

    if (fileType === 'pdf') {
      return (
        <div className="relative w-full h-full">
          {showFullscreenButton && (
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 p-2 rounded-sm shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Fullscreen"
            >
              <Maximize className={cn(iconSize.lg, "text-gray-700 dark:text-gray-300")} />
            </button>
          )}
          <iframe
            src={`${fileUrl}#toolbar=1&navpanes=1`}
            className="w-full h-full border-0"
            title={fileName}
          />
        </div>
      );
    } else if (fileType === 'image') {
      return (
        <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-900 p-4 relative">
          {showFullscreenButton && (
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 p-2 rounded-sm shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Fullscreen"
            >
              <Maximize className={cn(iconSize.lg, "text-gray-700 dark:text-gray-300")} />
            </button>
          )}
          <img
            src={fileUrl}
            alt={fileName}
            className="max-w-full max-h-full object-contain rounded-sm shadow-md"
          />
        </div>
      );
    } else if (fileType === 'ppt' || fileType === 'pptx') {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-100 dark:bg-gray-900 p-6">
          <div className={cn(card, "p-6 max-w-md w-full")}>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-orange-500 text-white text-sm flex items-center justify-center w-10 h-10 rounded-sm">
                PPT
              </div>
              <h3 className="ml-3 font-bold text-lg text-gray-900 dark:text-white">
                {fileName}
              </h3>
            </div>
            <p className={cn(bodySecondary, "mb-6")}>
              PowerPoint presentations cannot be previewed directly. You can download the file to view it.
            </p>
            <div className="flex justify-center">
              <a
                href={fileUrl}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-accent1 hover:bg-accent1/90 text-white px-4 py-2 rounded-sm flex items-center gap-2 transition-colors"
              >
                <FileText className={iconSize.lg} />
                Download Presentation
              </a>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-100 dark:bg-gray-900 p-6">
          <div className={cn(card, "p-6 max-w-md w-full")}>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gray-500 text-white text-sm flex items-center justify-center w-10 h-10 rounded-sm">
                FILE
              </div>
              <h3 className="ml-3 font-bold text-lg text-gray-900 dark:text-white">
                {fileName}
              </h3>
            </div>
            <p className={cn(bodySecondary, "mb-6")}>
              This file type cannot be previewed. You can download it to view.
            </p>
            <div className="flex justify-center">
              <a
                href={fileUrl}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-accent1 hover:bg-accent1/90 text-white px-4 py-2 rounded-sm flex items-center gap-2 transition-colors"
              >
                <FileText className={iconSize.lg} />
                Download File
              </a>
            </div>
          </div>
        </div>
      );
    }
  };

  // Derive rich status from assignment data
  const getAssignmentMeta = (assignment) => {
    const sub = assignment.studentSubmission;
    const isPastDue = new Date(assignment.dueDate) < new Date();
    const questionCount = assignment.questions?.length || 0;

    if (sub?.status === 'graded' || (sub?.grade !== null && sub?.grade !== undefined)) {
      return { label: `${sub.grade}/${assignment.totalPoints}`, style: statusStyles.submitted };
    }
    if (sub?.status === 'returned') {
      return { label: 'Returned', style: statusStyles.returned || 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-900/20 dark:border-orange-800' };
    }
    if (sub) {
      return { label: 'Submitted', style: statusStyles.submitted };
    }
    if (isPastDue) {
      return { label: 'Missing', style: 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/20 dark:border-red-800' };
    }
    return { label: 'Pending', style: statusStyles.in_progress };
  };

  const accentColor = getCardColor(selectedAssignment.title);

  // Sidebar content — shared between desktop and mobile drawer
  const renderSidebarContent = () => (
    <div className="space-y-1">
      {assignments.map((assignment, index) => {
        const isSelected = selectedAssignment?._id === assignment._id;
        const itemAccent = getCardColor(assignment.title);
        const meta = getAssignmentMeta(assignment);
        const shortDate = formatDateShort(assignment.dueDate);

        return (
          <motion.div
            key={assignment._id}
            {...motionStaggerChild(index)}
            onClick={() => handleAssignmentSelect(assignment, index)}
            className={cn(
              "relative py-3 px-3 rounded-sm cursor-pointer transition-all duration-200 group/item",
              isSelected
                ? "bg-accent1/8 dark:bg-accent1/15 shadow-sm"
                : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
            )}
          >
            {/* Active indicator bar */}
            <div
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-sm transition-all duration-200",
                isSelected ? "h-8 opacity-100" : "h-0 opacity-0 group-hover/item:h-4 group-hover/item:opacity-40"
              )}
              style={{ backgroundColor: itemAccent }}
            />

            <h3 className={cn(
              "text-sm truncate leading-snug mb-1",
              isSelected
                ? "font-bold text-gray-900 dark:text-white"
                : "font-medium text-gray-600 dark:text-gray-300"
            )}>
              {assignment.title}
            </h3>

            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={cn(badgeBase, meta.style, "text-[10px] px-1.5 py-px")}>
                {meta.label}
              </span>
              {shortDate && (
                <span className="text-[11px] text-gray-400 dark:text-gray-500">{shortDate}</span>
              )}
              {!assignment.isUngraded && (
                <span className="text-[11px] text-gray-400 dark:text-gray-500">{assignment.totalPoints} pts</span>
              )}
              {assignment.completeIn && (
                <span className="flex items-center gap-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                  <Clock className="w-2.5 h-2.5" />{assignment.completeIn}m
                </span>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className="flex min-h-[60vh] relative">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="md:hidden fixed top-20 left-4 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm p-2 shadow-sm hover:shadow-md transition-shadow"
        aria-label="Open assignment list"
      >
        <Menu className={iconSize.lg} />
      </button>

      {/* Mobile sidebar drawer */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40 z-40 md:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-50 md:hidden shadow-xl flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className={cn(cardTitle, "text-sm")}>Assignments</h2>
                  <div className={cn(accentBarSection, "w-8 mt-1.5")} />
                </div>
                <button onClick={() => setMobileSidebarOpen(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors">
                  <X className={iconSize.lg} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {renderSidebarContent()}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop sidebar — fixed to screen, own scroll */}
      <div className="hidden md:flex flex-col w-60 flex-shrink-0 sticky top-0 h-[calc(100vh-4rem)] border-r border-gray-200 dark:border-gray-700 pl-2 pr-4">
        <div className="pt-2 pb-3">
          <div className="flex items-center justify-between mb-1">
            <h2 className={cn(microLabel)}>Assignments</h2>
            <span className={cn(microLabel, "text-[10px]")}>{assignments.length}</span>
          </div>
          <div className={cn(accentBarSection, "w-8 mt-1.5")} />
        </div>
        <div className="flex-1 overflow-y-auto -mr-2 pr-2">
          {renderSidebarContent()}
        </div>
      </div>

      {/* Main Content — scrolls independently */}
      <div className="flex-1 min-w-0 md:pl-5 pl-0">
        <div className="space-y-4">
          {/* Assignment Header */}
          <div className={cn(card, "overflow-hidden")}>
            <div className="h-1.5" style={{ backgroundColor: accentColor }} />
            <div className="px-5 py-4">
              {/* Title row — title left, grade/status right */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl sm:text-2xl font-black tracking-tight text-gray-900 dark:text-white leading-tight">
                    {selectedAssignment.title}
                  </h1>
                </div>

                {/* Grade / Status — right side */}
                <div className="flex-shrink-0">
                  {submission && submission.status === 'returned' ? (
                    <span className={cn(badgeBase, statusStyles.returned)}>Returned</span>
                  ) : submission && submission.grade !== null && submission.grade !== undefined ? (() => {
                    const pct = Math.round((submission.grade / selectedAssignment.totalPoints) * 100);
                    const radius = 30;
                    const stroke = 5;
                    const circumference = 2 * Math.PI * radius;
                    const offset = circumference - (pct / 100) * circumference;
                    const strokeColor = pct >= 75 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
                    return (
                      <div className="flex items-center gap-3">
                        <div className="relative w-[72px] h-[72px]">
                          <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
                            <circle cx="36" cy="36" r={radius} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-gray-200 dark:text-gray-700" />
                            <motion.circle
                              cx="36" cy="36" r={radius} fill="none"
                              stroke={strokeColor} strokeWidth={stroke}
                              strokeLinecap="round"
                              strokeDasharray={circumference}
                              initial={{ strokeDashoffset: circumference }}
                              animate={{ strokeDashoffset: offset }}
                              transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-lg font-black tracking-tight text-gray-900 dark:text-white leading-none">{pct}%</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-xl font-black tracking-tight text-gray-900 dark:text-white leading-none">
                            {submission.grade}<span className="text-xs font-bold text-gray-400 dark:text-gray-500">/{selectedAssignment.totalPoints}</span>
                          </span>
                          <span className={cn(microLabel, "mt-1")}>Score</span>
                        </div>
                      </div>
                    );
                  })() : submission ? (
                    <div className="flex flex-col items-end gap-1">
                      <span className={cn(badgeBase, statusStyles.submitted)}>Submitted</span>
                      {(submission.grade === null || submission.grade === undefined) && subjectiveQuestions.length > 0 && !selectedAssignment.isUngraded && (
                        <span className="flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400">
                          <Clock className={iconSize.xs} />
                          Awaiting review
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Description / Instructions — right below title */}
              {selectedAssignment.description && (
                <p className={cn(bodySecondary, "mt-2 text-sm leading-relaxed")}>
                  {selectedAssignment.description}
                </p>
              )}

              {/* Feedback */}
              {submission && submission.feedback && (
                <div className={cn("mt-3 pt-3 border-t", dividerInner)}>
                  <div className={cn(microLabel, "mb-1")}>Feedback</div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{submission.feedback}</p>
                </div>
              )}

              {/* Submission file link */}
              {submission && submission.submissionFile && (
                <div className="mt-2">
                  <a
                    href={submission.submissionFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent1 hover:text-accent1/80 inline-flex items-center gap-1 font-medium transition-colors"
                  >
                    <FileText className={iconSize.xs} />
                    View Submission File
                  </a>
                </div>
              )}

              {/* Date, time, points — at the end */}
              <div className={cn("flex items-center gap-3 mt-3 pt-3 border-t flex-wrap", dividerInner)}>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <Calendar className={iconSize.xs} />
                  <span>{formatDate(selectedAssignment.dueDate)}</span>
                </div>
                <div className="w-px h-3 bg-gray-200 dark:bg-gray-700" />
                {selectedAssignment.isUngraded ? (
                  <span className={cn(badgeBase, statusStyles.not_started, "text-[10px] py-px")}>Ungraded</span>
                ) : (
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{selectedAssignment.totalPoints} pts</span>
                )}
                {questions.length > 0 && (
                  <>
                    <div className="w-px h-3 bg-gray-200 dark:bg-gray-700" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {subjectiveQuestions.length > 0 && `${subjectiveQuestions.length} subjective`}
                      {subjectiveQuestions.length > 0 && objectiveQuestions.length > 0 && ', '}
                      {objectiveQuestions.length > 0 && `${objectiveQuestions.length} MCQ`}
                    </span>
                  </>
                )}
                {selectedAssignment.completeIn && !submission && (
                  <>
                    <div className="w-px h-3 bg-gray-200 dark:bg-gray-700" />
                    <span className="flex items-center gap-1 text-xs text-accent1 font-medium">
                      <Clock className={iconSize.xs} />
                      {selectedAssignment.completeIn} min
                    </span>
                  </>
                )}
                {submission && submission.objectiveScore !== undefined && submission.totalObjectivePoints > 0 && (
                  <>
                    <div className="w-px h-3 bg-gray-200 dark:bg-gray-700" />
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">MCQ: {submission.objectiveScore}/{submission.totalObjectivePoints}</span>
                  </>
                )}
              </div>
            </div>

            {/* Start Assignment Prompt — compact inline bar */}
            {selectedAssignment.completeIn && !assignmentStarted && !submission && (
              <div className="px-4 py-3 border-t border-accent1/20 bg-accent1/5 dark:bg-accent1/10 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Clock className="h-4 w-4 text-accent1 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">Timed — {selectedAssignment.completeIn} min</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Cannot be paused once started</span>
                  </div>
                </div>
                <button
                  onClick={() => handleStartAssignment(selectedAssignment)}
                  className="px-4 py-1.5 bg-accent1 hover:bg-accent1/90 text-white rounded-sm transition-colors text-sm font-medium flex-shrink-0"
                >
                  Start
                </button>
              </div>
            )}

            {/* Timer Display — compact bar at bottom of header */}
            {selectedAssignment.completeIn && !submission && assignmentStarted && (
              <motion.div
                className={cn(
                  "px-4 py-2.5 border-t flex items-center justify-between",
                  timeRemaining && timeRemaining < 2 * 60 * 1000
                    ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-700'
                    : timeRemaining && timeRemaining < 5 * 60 * 1000
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : timeRemaining && timeRemaining < 10 * 60 * 1000
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                    : 'bg-accent1/5 dark:bg-accent1/10 border-accent1/20'
                )}
                animate={timeRemaining && timeRemaining < 2 * 60 * 1000 ? { opacity: [1, 0.7, 1] } : {}}
                transition={timeRemaining && timeRemaining < 2 * 60 * 1000 ? { duration: 1.2, repeat: Infinity } : {}}
              >
                <div className="flex items-center gap-2">
                  <Clock className={cn(
                    "h-4 w-4",
                    timeRemaining && timeRemaining < 5 * 60 * 1000
                      ? 'text-red-600 dark:text-red-400 animate-pulse'
                      : timeRemaining && timeRemaining < 10 * 60 * 1000
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-accent1'
                  )} />
                  <span className={cn(
                    "text-2xl font-black font-mono tracking-tight",
                    timeRemaining && timeRemaining < 5 * 60 * 1000
                      ? 'text-red-600 dark:text-red-400'
                      : timeRemaining && timeRemaining < 10 * 60 * 1000
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-accent1'
                  )}>
                    {timeRemaining ? formatTimeRemaining(timeRemaining) : '--:--:--'}
                  </span>
                </div>
                {timeRemaining && timeRemaining < 5 * 60 * 1000 && (
                  <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <AlertTriangle className={iconSize.sm} />
                    <span className="text-xs font-bold">Running out!</span>
                  </div>
                )}
                {isTimeUp && (
                  <span className="text-xs font-bold text-red-600 dark:text-red-400">Submitting...</span>
                )}
              </motion.div>
            )}
          </div>

          {/* Returned Submission Banner */}
          {submission && submission.status === 'returned' && (
            <div className={cn(card, "overflow-hidden")}>
              <div className="flex items-center justify-between px-4 py-2.5 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  <span className="text-sm font-bold text-orange-800 dark:text-orange-300">Returned for Revision</span>
                </div>
                {submission.revertedAt && (
                  <span className="text-[11px] text-orange-500 dark:text-orange-400">{new Date(submission.revertedAt).toLocaleDateString()}</span>
                )}
              </div>
              <div className="px-4 py-3 space-y-2">
                {submission.revertReason && (
                  <div>
                    <div className={cn(microLabel, "text-orange-700 dark:text-orange-400 mb-0.5")}>Reason</div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{submission.revertReason}</p>
                  </div>
                )}
                <p className="text-xs text-orange-600 dark:text-orange-400">Please review feedback and resubmit.</p>
              </div>
            </div>
          )}


          {/* Tabs Section - Only show if there are attachments */}
          {hasAttachments && questions.length > 0 && (
            <div className={cn(card, "overflow-hidden")}>
              <div className={cn("border-b", dividerInner)}>
                <nav className="flex space-x-1 px-4" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('questions')}
                    className={cn(
                      "px-4 py-3 text-sm font-medium transition-colors",
                      activeTab === 'questions'
                        ? 'border-b-2 border-accent1 text-accent1'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    )}
                  >
                    Questions
                  </button>
                  <button
                    onClick={() => setActiveTab('attachments')}
                    className={cn(
                      "px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2",
                      activeTab === 'attachments'
                        ? 'border-b-2 border-accent1 text-accent1'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    )}
                  >
                    <Paperclip className={iconSize.md} />
                    Attachments ({attachments.length})
                  </button>
                </nav>
              </div>
            </div>
          )}

          {/* Questions Section */}
          {activeTab === 'questions' && (
            <>
              {!assignmentStarted && selectedAssignment.completeIn && !submission ? (
                <div className={cn(card, "px-4 py-3 text-center")}>
                  <p className={bodySecondary}>Start the assignment to view questions.</p>
                </div>
              ) : questions.length === 0 ? (
                <div className={cn(card, "px-4 py-3 text-center")}>
                  <p className={bodySecondary}>No questions for this assignment.</p>
                </div>
              ) : (
                <div className="space-y-3">
              {/* Subjective Questions */}
              {subjectiveQuestions.length > 0 && (
                <div className={cn(card, "overflow-hidden")}>
                  <div className="p-5">
                    <h2 className={cn(cardTitle, "text-sm")}>
                      Subjective Questions
                    </h2>
                    <div className={cn(accentBarSection, "w-8 mt-1.5 mb-4")} />
                    <div className="space-y-4">
                      {subjectiveQuestions.map((q, index) => {
                        const qId = q._id || q.id;
                        const answerValue = subjectiveAnswers[qId] || '';
                        return (
                          <motion.div
                            key={qId}
                            {...motionStaggerChild(index)}
                            className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-sm p-5"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <span className="bg-accent1 text-white w-9 h-9 rounded-sm text-sm font-black flex items-center justify-center shadow-sm">
                                  {index + 1}
                                </span>
                                <div>
                                  <span className="text-xs font-bold text-gray-900 dark:text-white">Question {index + 1}</span>
                                  <span className={cn(bodySecondary, "ml-2")}>{q.points || 0} pts</span>
                                </div>
                              </div>
                              {/* Inline grade for this question */}
                              {submission && submission.questionGrades && submission.questionGrades[qId] !== undefined && (
                                <span className={cn(
                                  "text-sm font-black px-2.5 py-1 rounded-sm",
                                  (submission.questionGrades[qId] / (q.points || 1)) >= 0.75
                                    ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                                    : (submission.questionGrades[qId] / (q.points || 1)) >= 0.5
                                    ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                                    : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                                )}>
                                  {submission.questionGrades[qId]}/{q.points}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-900 dark:text-white font-medium mb-4 leading-relaxed text-[15px]">
                              {q.question}
                            </p>
                            <textarea
                              value={answerValue}
                              onChange={(e) => handleSubjectiveAnswerChange(qId, e.target.value)}
                              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-sm focus:ring-2 focus:ring-accent1 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                              rows="6"
                              placeholder="Type your answer here..."
                              disabled={(!!submission && submission.status !== 'returned') || !assignmentStarted}
                            />
                            {submission && answerValue && (
                              <p className={cn(bodySecondary, "mt-2")}>
                                Your answer is saved
                              </p>
                            )}
                          </motion.div>
                        );
                      })}

                      {/* Upload Option for Subjective */}
                      {(!submission || submission.status === 'returned') && assignmentStarted && (
                        <div className={cn(emptyState)}>
                          <Upload className={cn(iconSize.empty, "mx-auto text-gray-400 dark:text-gray-500 mb-3")} />
                          <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                            Upload Submission File (Optional)
                          </h3>
                          <p className={cn(bodySecondary, "mb-4")}>
                            You can upload a file (PDF, DOC, DOCX, JPEG, PNG — max 10 MB) with your answers instead of or in addition to text answers.
                          </p>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                            className="hidden"
                            id="pdf-upload"
                            disabled={!assignmentStarted}
                          />
                          <label
                            htmlFor="pdf-upload"
                            className={cn(
                              "inline-flex items-center px-4 py-2 rounded-sm cursor-pointer transition-colors",
                              assignmentStarted
                                ? 'bg-accent1 hover:bg-accent1/90 text-white'
                                : 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                            )}
                          >
                            <Upload className={cn(iconSize.md, "mr-2")} />
                            {selectedFile ? selectedFile.name : "Choose File"}
                          </label>
                          {selectedFile && (
                            <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2 font-medium">
                              File selected
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Objective Questions */}
              {objectiveQuestions.length > 0 && (
                <div className={cn(card, "overflow-hidden")}>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h2 className={cn(cardTitle, "text-sm")}>
                        Objective Questions (MCQ)
                      </h2>
                      <span className={cn(badgeBase, "text-[10px] py-px text-accent1 bg-accent1/5 border-accent1/20")}>Auto-scored</span>
                    </div>
                    <div className={cn(accentBarSection, "w-8 mt-1.5 mb-4")} />
                    <div className="space-y-4">
                      {objectiveQuestions.map((q, index) => {
                        const qId = q._id || q.id;
                        const selectedAnswer = objectiveAnswers[qId];
                        const isCorrect = selectedAnswer === q.correctAnswer;

                        return (
                          <motion.div
                            key={qId}
                            {...motionStaggerChild(index)}
                            className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-sm p-5"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <span className="bg-emerald-600 text-white w-9 h-9 rounded-sm text-sm font-black flex items-center justify-center shadow-sm">
                                  {index + 1}
                                </span>
                                <div>
                                  <span className="text-xs font-bold text-gray-900 dark:text-white">Question {index + 1}</span>
                                  <span className={cn(bodySecondary, "ml-2")}>{q.points || 0} pts</span>
                                </div>
                              </div>
                              {/* Inline score for this MCQ */}
                              {submission && selectedAnswer && (
                                <span className={cn(
                                  "text-sm font-black px-2.5 py-1 rounded-sm",
                                  isCorrect
                                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                )}>
                                  {isCorrect ? `${q.points}/${q.points}` : `0/${q.points}`}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-900 dark:text-white font-medium mb-4 leading-relaxed text-[15px]">
                              {q.question}
                            </p>
                            <div className="space-y-2.5">
                              {q.options?.map((option, optIndex) => {
                                const isSelected = selectedAnswer === option;
                                return (
                                  <label
                                    key={optIndex}
                                    className={cn(
                                      "flex items-center gap-3 p-3 border-2 rounded-sm cursor-pointer transition-all duration-200",
                                      isSelected
                                        ? 'border-accent1 bg-accent1/5 dark:bg-accent1/10 shadow-sm'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-accent1/40 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                    )}
                                  >
                                    <input
                                      type="radio"
                                      name={`question_${qId}`}
                                      value={option}
                                      checked={isSelected}
                                      onChange={() => handleObjectiveAnswerChange(qId, option)}
                                      disabled={(!!submission && submission.status !== 'returned') || !assignmentStarted}
                                      className="w-4 h-4 text-accent1"
                                    />
                                    <span className="font-medium text-gray-900 dark:text-white">
                                      {String.fromCharCode(65 + optIndex)}.
                                    </span>
                                    <span className="flex-1 text-gray-900 dark:text-white">
                                      {option}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit / Resubmit Button */}
              {(!submission || submission.status === 'returned') && assignmentStarted && (
                <motion.button
                  onClick={handleSubmit}
                  disabled={isTimeUp}
                  whileHover={!isTimeUp ? { scale: 1.01 } : {}}
                  whileTap={!isTimeUp ? { scale: 0.98 } : {}}
                  className={cn(
                    "w-full px-5 py-3 rounded-sm flex items-center justify-center gap-2.5 text-sm font-bold tracking-wide uppercase transition-colors shadow-sm",
                    isTimeUp
                      ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                      : 'bg-accent1 hover:bg-accent1/90 text-white hover:shadow-md'
                  )}
                >
                  <Send className={iconSize.md} />
                  {isTimeUp ? 'Submitting...' : submission?.status === 'returned' ? 'Resubmit Assignment' : 'Submit Assignment'}
                </motion.button>
              )}
            </div>
          )}
            </>
          )}

          {/* Attachments Section */}
          {activeTab === 'attachments' && hasAttachments && (
            <div className={cn(card, "overflow-hidden")}>
              <div className="flex flex-col h-[calc(100vh-300px)] min-h-[600px]">
                {/* Attachment List */}
                {attachments.length > 1 && (
                  <div className={cn("border-b p-4", dividerInner)}>
                    <h3 className={cn(microLabel, "mb-3")}>
                      Select Attachment
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((attachment, index) => {
                        const isSelected = selectedAttachmentIndex === index;
                        return (
                          <button
                            key={attachment._id || index}
                            onClick={() => setSelectedAttachmentIndex(index)}
                            className={cn(
                              "px-4 py-2 rounded-sm text-sm font-medium transition-colors flex items-center gap-2",
                              isSelected
                                ? 'bg-accent1 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            )}
                          >
                            <FileText className={iconSize.md} />
                            <span className="max-w-[200px] truncate">
                              {attachment.name || `Attachment ${index + 1}`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Viewer */}
                <div className="flex-1 relative">
                  {attachments[selectedAttachmentIndex] && renderAttachmentViewer(attachments[selectedAttachmentIndex], true)}
                </div>
              </div>
            </div>
          )}

          {/* Show attachments if no questions but has attachments */}
          {questions.length === 0 && hasAttachments && (
            <div className={cn(card, "overflow-hidden")}>
              <div className="flex flex-col h-[calc(100vh-300px)] min-h-[600px]">
                {attachments.length > 1 && (
                  <div className={cn("border-b p-4", dividerInner)}>
                    <h3 className={cn(microLabel, "mb-3")}>
                      Select Attachment
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((attachment, index) => {
                        const isSelected = selectedAttachmentIndex === index;
                        return (
                          <button
                            key={attachment._id || index}
                            onClick={() => setSelectedAttachmentIndex(index)}
                            className={cn(
                              "px-4 py-2 rounded-sm text-sm font-medium transition-colors flex items-center gap-2",
                              isSelected
                                ? 'bg-accent1 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            )}
                          >
                            <FileText className={iconSize.md} />
                            <span className="max-w-[200px] truncate">
                              {attachment.name || `Attachment ${index + 1}`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Viewer */}
                <div className="flex-1 relative">
                  {attachments[selectedAttachmentIndex] && renderAttachmentViewer(attachments[selectedAttachmentIndex], true)}
                </div>
              </div>
            </div>
          )}

          {/* Fullscreen Modal */}
          {isFullscreen && attachments[selectedAttachmentIndex] && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 bg-gray-900 text-white">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-bold">
                    {attachments[selectedAttachmentIndex].name || `Attachment ${selectedAttachmentIndex + 1}`}
                  </h3>
                  {attachments.length > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedAttachmentIndex(Math.max(0, selectedAttachmentIndex - 1))}
                        disabled={selectedAttachmentIndex === 0}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <span className="text-sm">
                        {selectedAttachmentIndex + 1} / {attachments.length}
                      </span>
                      <button
                        onClick={() => setSelectedAttachmentIndex(Math.min(attachments.length - 1, selectedAttachmentIndex + 1))}
                        disabled={selectedAttachmentIndex === attachments.length - 1}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="p-2 hover:bg-gray-700 rounded-sm transition-colors"
                  title="Exit Fullscreen (Esc)"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Fullscreen Viewer */}
              <div className="flex-1 overflow-auto">
                {renderAttachmentViewer(attachments[selectedAttachmentIndex], false)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAssignmentSection;
